import { useEffect } from "react";
import { ArrowUpRight, ExternalLink, Award } from "lucide-react";
import type { Advertisement } from "../types";
import { apiSend } from "../services/api";

type AdSlotProps = {
  ads?: Advertisement[] | null;
  placement: string;
  className?: string;
};

export function AdSlot({ ads, placement, className = "" }: AdSlotProps) {
  const ad = ads?.find((item) => item.placement === placement && item.active !== false);

  useEffect(() => {
    if (ad) {
      apiSend(`/api/advertisements/${ad.id}/event`, {
        method: "POST",
        body: JSON.stringify({ event_type: "impression", placement })
      }).catch(() => undefined);
    }
  }, [ad?.id, placement]);

  if (!ad) return null;

  const trackClick = () => {
    apiSend(`/api/advertisements/${ad.id}/event`, {
      method: "POST",
      body: JSON.stringify({ event_type: "click", placement })
    }).catch(() => undefined);
  };

  // Video creatives: muted, inline, looped; explicit click-through below the player.
  if (ad.ad_type === "video" && ad.video_url) {
    return (
      <div className={`mag-ad-container video-type ${className}`}>
        <div className="mag-ad-disclosure-bar"><span className="mag-ad-disclosure-text">ADVERTISEMENT · VIDEO</span>{ad.sponsor_name && <span className="mag-ad-sponsor-tag">{ad.sponsor_name}</span>}</div>
        <video className="mag-video-ad-asset" src={ad.video_url} poster={ad.image_url || undefined} muted autoPlay loop playsInline controls preload="metadata" aria-label={ad.alt_text || ad.name} />
        <a className="mag-video-ad-link" href={ad.destination_url} rel="sponsored noopener noreferrer" target="_blank" onClick={trackClick}>{ad.cta_text || "Visit Sponsor"} <ArrowUpRight size={15} /></a>
      </div>
    );
  }

  // Case 1: Custom Raw HTML Ad
  if (ad.ad_type === "html" && ad.html_content) {
    return (
      <div className={`mag-ad-container html-type ${className}`}>
        <div className="mag-ad-disclosure-bar">
          <div className="flex items-center gap-2">
            <span className="mag-ad-disclosure-text">SPONSORED ANNOUNCEMENT</span>
            {ad.sponsor_name && <span className="mag-ad-sponsor-tag">{ad.sponsor_name}</span>}
          </div>
        </div>
        <div
          className="mag-ad-html-inner"
          dangerouslySetInnerHTML={{ __html: ad.html_content }}
          onClick={trackClick}
        />
      </div>
    );
  }

  // Case 2: Native Text / Editorial Sponsor Ad
  if (ad.ad_type === "text" || (!ad.image_url && (ad.headline || ad.body_text))) {
    return (
      <div className={`mag-ad-container text-type ${className}`}>
        <div className="mag-ad-disclosure-bar">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Award size={11} className="text-amber-600" />
              <span className="mag-ad-disclosure-text">SPONSORED REPORT</span>
            </div>
            {ad.sponsor_name && <span className="mag-ad-sponsor-tag">Presented by {ad.sponsor_name}</span>}
          </div>
        </div>

        <a
          href={ad.destination_url}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={trackClick}
          className="mag-native-ad-body"
        >
          <div className="mag-native-ad-content">
            <h4 className="mag-native-ad-title">{ad.headline || ad.name}</h4>
            {ad.body_text && <p className="mag-native-ad-desc">{ad.body_text}</p>}
          </div>
          <div className="mag-native-ad-action">
            <span className="mag-native-cta-btn">
              <span>{ad.cta_text || "Learn More"}</span>
              <ArrowUpRight size={14} />
            </span>
          </div>
        </a>
      </div>
    );
  }

  // Case 3: Standard Display / Banner Image Ad
  return (
    <div className={`mag-ad-container image-type ${className}`}>
      <div className="mag-ad-disclosure-bar">
        <span className="mag-ad-disclosure-text">ADVERTISEMENT</span>
        {ad.sponsor_name && <span className="mag-ad-sponsor-tag">{ad.sponsor_name}</span>}
      </div>
      <a
        className="mag-image-ad-link"
        href={ad.destination_url}
        target="_blank"
        rel="sponsored noopener noreferrer"
        onClick={trackClick}
      >
        {ad.image_url ? (
          <img
            src={ad.image_url}
            alt={ad.alt_text || ad.name}
            loading="lazy"
            className="mag-image-ad-asset"
          />
        ) : (
          <div className="mag-image-ad-fallback">
            <span className="font-serif font-bold text-slate-800">{ad.name}</span>
            <span className="text-xs text-amber-700 font-semibold mt-1 flex items-center gap-1">
              Visit Sponsor <ExternalLink size={12} />
            </span>
          </div>
        )}
      </a>
    </div>
  );
}
